import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ApiError, usePackagesApi } from '../apis/packages-apis';
import { useProductsApi } from '../apis/products-apis'; // Import products API
import { Package } from '../types';
import { useToast } from "../components/ui/Toast";
import { useNavigation } from '../contexts/NavigationContext';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import PackageForm, { PackageFormRef } from './PackageForm';
import { Spinner } from "../components/ui/Spinner";

export const EditPackagePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { addToast } = useToast();
  const initialFetchMade = useRef(false);
  const formRef = useRef<PackageFormRef>(null);
  
  const [packageAgenticgenie, setPackage] = useState<Package | null>(null);
  const [originalPackage, setOriginalPackage] = useState<Package | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [existingPackages, setExistingPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();
  const packagesApi = usePackagesApi();
  const productsApi = useProductsApi();

  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(packageAgenticgenie) !== JSON.stringify(originalPackage);

  // Handle browser back button
  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }, [hasUnsavedChanges]);

  // Set up block for browser back button
  useEffect(() => {
    // Add extra history entry when editing starts
    if (hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
    }

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.pathname);
        // Show warning dialog
        setShowNavigationWarning(true);
        setPendingNavigation('/marketplace/packages');
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, handleBeforeUnload, setShowNavigationWarning, setPendingNavigation]);

  useEffect(() => {
    const fetchData = async () => {
      if (id && !initialFetchMade.current) {
        initialFetchMade.current = true;
        try {
          const getAllPackages = true;
          const [packageData, allPackages] = await Promise.all([
            packagesApi.getPackage(id),
            packagesApi.getPackages(getAllPackages)
          ]);
          setPackage(packageData);
          setOriginalPackage(packageData);
          setExistingPackages(allPackages);
        } catch (error: any) {
          console.error('Error fetching data:', error);
          
          if (error instanceof ApiError && error.status === 403) {
            navigate('/marketplace/unauthorized');
            return;
          }
          
          // For all other errors, show the specific error message or fallback
          const errorMessage = error instanceof ApiError 
            ? error.message 
            : "Failed to fetch package details";
            
          addToast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
            duration: 5000
          });
          navigate('/marketplace/packages');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [id, navigate, addToast]);

  const handleSubmit = async () => {
    if (!packageAgenticgenie) return;

    const isValid = await formRef.current?.validateForm() ?? false;
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    try {
      setIsSaving(true);
      // Check if product name has changed
      const productNameChanged = originalPackage?.packageProductName !== packageAgenticgenie.packageProductName;
      
      // Handle product creation/update if needed
      if (productNameChanged && packageAgenticgenie.packageProductName) {
        try {
          // This will either find the existing product or create a new one
          await productsApi.findOrCreateProduct(packageAgenticgenie.packageProductName);
        } catch (productError) {
          console.error('Error handling product:', productError);
          addToast({
            title: "Warning",
            description: "There was an issue with the product, but the package update will proceed",
            variant: "default",
            duration: 4000
          });
          // Continue with package update despite product issues
        }
      }
      
      // Update the package
      await packagesApi.updatePackage(packageAgenticgenie);
      const savedPackage = { ...packageAgenticgenie };
      setOriginalPackage(savedPackage);
      setPackage(savedPackage);
      setIsReadOnly(true);
      
      addToast({
        title: "Success",
        description: "Package " + packageAgenticgenie.packageName + " has been successfully updated",
        variant: "default",
        duration: 4000
      });

      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Error updating package:', error);
      addToast({
        title: "Error",
        description: "Failed to update package: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(null);
    } else {
      setPackage(originalPackage);
      setIsReadOnly(true);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    setPackage(originalPackage);
    setIsReadOnly(true);
    
    if (pendingNavigation) {
      // Go back twice to handle the extra history entry we added
      window.history.go(-2);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/marketplace/packages');
    } else {
      navigate('/marketplace/packages');
    }
  };

  if (!packageAgenticgenie) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading Package...</p>
      </div>
    );
  }

  return (
    <>
      <PackageForm 
        ref={formRef}
        package={packageAgenticgenie}
        originalPackage={originalPackage}
        onPackageChange={setPackage}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={isReadOnly}
        onEditClick={() => setIsReadOnly(false)}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        existingPackages={existingPackages}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default EditPackagePage;